import type { CodeToolSettings } from "@repo/app-types/entities/tools/code";

type SavedTestInput = NonNullable<CodeToolSettings["test_input"]>;

/** A code tool's test input as the form edits it. */
export interface CodeToolTestInputFormSettings {
  /** JSON text; empty when the tool keeps none, and the starter shows. */
  testInput: string;
  testInputError: string | undefined;
}

export function initializeCodeToolTestInputFormSettings(
  testInput: SavedTestInput | undefined,
): CodeToolTestInputFormSettings {
  return {
    testInput: testInput ? JSON.stringify(testInput, null, 2) : "",
    testInputError: undefined,
  };
}

/** The input as an object, or what is wrong with it. */
export function parseTestInput(text: string): SavedTestInput | string {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return "Enter valid JSON.";
  }
  if (!isJsonObject(parsed)) {
    return "Enter a JSON object.";
  }
  return parsed;
}

function isJsonObject(value: unknown): value is SavedTestInput {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

type CodeToolTestInputSaveResult =
  | { isValid: true; testInput: SavedTestInput | undefined }
  | { isValid: false; testInputError: string };

/**
 * The test input to save, `undefined` when the input is empty. Any JSON object
 * saves; whether it fits the script's input is for a test run to say.
 */
export function buildCodeToolTestInput(
  settings: CodeToolTestInputFormSettings,
): CodeToolTestInputSaveResult {
  if (settings.testInput.trim() === "") {
    return { isValid: true, testInput: undefined };
  }
  const parsed = parseTestInput(settings.testInput);
  if (typeof parsed === "string") {
    return { isValid: false, testInputError: parsed };
  }
  return { isValid: true, testInput: parsed };
}
